import '../scss/components/_ownercompliance.scss'
import React, { useState, useEffect } from 'react'
import { 
  getAllComplianceByPGID, 
  updateComplianceStatus, 
  getTenantRegisterByAdmission,
  getTenantComplianceRecords,
  getAgreementJsonData 
} from '../apis/services/pgApi'
import ViewAgreementModal from './RentAgreement'
import TenantAgreementModalView from './TenantAgreementModalView'
import * as XLSX from 'xlsx'
import { Verified } from 'lucide-react'

// ─── Constants ────────────────────────────────────────────────────
const POLICE_CFG = {
  COMPLETED: { label: 'Completed', color: '#16A34A', dot: '#22C55E' },
  Verified: { label: 'Verified', color: '#16A34A', dot: '#22C55E' },
  PENDING: { label: 'Pending', color: '#D97706', dot: '#F59E0B' },
  REJECTED: { label: 'Rejected', color: '#DC2626', dot: '#EF4444' },
  IN_PROGRESS: { label: 'In Progress', color: '#3B82F6', dot: '#60A5FA' },
}

const AGREEMENT_CFG = {
  active: { label: 'Active', bg: '#F0FDF4', text: '#166534', border: '#86EFAC' },
  NA: { label: 'Not Available', bg: '#FFF1F2', text: '#991B1B', border: '#FECACA' },
  expiring_soon: { label: 'Expiring Soon', bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
}

const PAGE_SIZE = 5

// ─── Helper Functions ─────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatDateFull = (dateString) => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { 
    weekday: 'long',
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

// Converts any parseable date value into the yyyy-mm-dd format required by <input type="date">
const toInputDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  return date.toISOString().split('T')[0]
}

// ─── Excel Download Helper ────────────────────────────────────────────
const downloadExcel = (data, fileName) => {
  if (!data || data.length === 0) {
    alert('No data to download')
    return
  }

  try {
    const ws = XLSX.utils.json_to_sheet(data)
    
    const colWidths = []
    const headers = Object.keys(data[0])
    headers.forEach((key, index) => {
      let maxLength = key.length
      data.forEach(row => {
        const value = row[key] !== undefined && row[key] !== null ? String(row[key]) : ''
        if (value.length > maxLength) maxLength = value.length
      })
      colWidths.push({ wch: Math.min(Math.max(maxLength + 4, 12), 40) })
    })
    ws['!cols'] = colWidths

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Tenant Register')
    XLSX.writeFile(wb, `${fileName}.xlsx`)
  } catch (error) {
    console.error('Error downloading Excel:', error)
    alert('Failed to download Excel file')
  }
}

// ─── Tenant Field (view / edit toggle) ─────────────────────────────
const TenantField = ({ label, displayValue, editValue, isEditing, editable = true, type = 'text', options, onChange }) => (
  <div className="tenant-detail-item">
    <span className="tenant-detail-label">{label}</span>
    {isEditing && editable ? (
      type === 'select' ? (
        <select
          className="tenant-detail-input"
          value={editValue || ''}
          onChange={e => onChange(e.target.value)}
        >
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : (
        <input
          className="tenant-detail-input"
          type={type}
          value={editValue || ''}
          onChange={e => onChange(e.target.value)}
        />
      )
    ) : (
      <span className="tenant-detail-value">{displayValue || '—'}</span>
    )}
  </div>
)

// ─── Tenant Register Modal ─────────────────────────────────────────
const TenantRegisterModal = ({ tenantData, onClose, loading }) => {
  // savedOverride holds the last successfully-saved edits so the modal reflects
  // the update immediately, without needing the parent to refetch.
  const [savedOverride, setSavedOverride] = useState(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedData, setEditedData] = useState(null)
  const [saving, setSaving] = useState(false)

  const rawData = Array.isArray(tenantData) ? tenantData[0] : tenantData
  const data = savedOverride || rawData

  // Reset local edit state whenever a new tenant is loaded into the modal
  useEffect(() => {
    setSavedOverride(null)
    setIsEditMode(false)
    setEditedData(rawData ? { ...rawData } : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantData])

  if (!tenantData) return null

  const handleDownloadSingle = () => {
    if (!data) {
      alert('No data to download')
      return
    }

    const excelData = {
      'Tenant ID': data.TenantId || '—',
      'Full Name': data.FullName || '—',
      'Gender': data.Gender || '—',
      'Date of Birth': data['Date Of Birth'] || '—',
      'Mobile Number': data.MobileNumber || '—',
      'Email Address': data.EmailId || '—',
      'Emergency Contact Name': data.EmergenctContactName || '—',
      'Emergency Contact Number': data.EmergencyContactNumber || '—',
      'PG Name': data.PGName || '—',
      'Room Number': data.RoomNumber || '—',
      'Bed Number': data.BedNumber || '—',
      'Joining Date': data.JoinDate || '—',
      'Notice Date': data.NoticeDate || '—',
      'Vacate Date': data.VacateDate || '—',
      'Exit Date': data.ExitDate || '—'
    }

    const fileName = `Tenant_Register_${data.FullName || 'Tenant'}_${data.RoomNumber || ''}`
    downloadExcel([excelData], fileName)
  }

  const handleEditClick = () => {
    setEditedData({ ...data })
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setEditedData({ ...data })
    setIsEditMode(false)
  }

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }))
  }

  const handleSaveEdit = async () => {
    setSaving(true)
    try {
      // TODO: wire this up to the real update-tenant API once it's available, e.g.
      // const response = await updateTenantRegister(data.TenantId, editedData)
      // if (!response.success) {
      //   alert(response.message || 'Failed to update tenant details')
      //   return
      // }

      setSavedOverride({ ...editedData })
      setIsEditMode(false)
    } catch (err) {
      console.error('Error updating tenant register:', err)
      alert('Failed to update tenant details')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="oc-modal-overlay" onClick={isEditMode ? undefined : onClose}>
      <div className="oc-modal oc-modal-large" onClick={e => e.stopPropagation()}>
        <div className="oc-modal-head">
          <div>
            <h3 className="oc-modal-title">📋 Tenant Register Details</h3>
            <p className="oc-modal-sub">{isEditMode ? 'Editing tenant information' : 'Complete tenant information'}</p>
          </div>
          <div className="oc-modal-head-actions">
            {!loading && data && !isEditMode && (
              <>
                <button
                  className="oc-edit-btn"
                  onClick={handleEditClick}
                  title="Edit tenant details"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Edit
                </button>
                <button 
                  className="oc-download-single-btn"
                  onClick={handleDownloadSingle}
                  title="Download this tenant's register"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                  </svg>
                  Download
                </button>
              </>
            )}
            <button className="oc-modal-close" onClick={onClose} disabled={isEditMode && saving}>
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="oc-modal-body oc-modal-body-no-scroll">
          {loading ? (
            <div className="oc-loading">
              <div className="oc-spinner"></div>
              <p>Loading tenant details...</p>
            </div>
          ) : !data ? (
            <div className="oc-modal-empty">
              <p>No tenant data found</p>
            </div>
          ) : (
            <div className="tenant-register-details">
              <div className="tenant-detail-row">
                <div className="tenant-detail-col">
                  <h4 className="tenant-detail-section-title">👤 Personal Information</h4>
                  <div className="tenant-detail-grid">
                    <TenantField
                      label="Tenant ID"
                      displayValue={data.TenantId}
                      editable={false}
                      isEditing={isEditMode}
                    />
                    <TenantField
                      label="Full Name"
                      displayValue={data.FullName}
                      editValue={editedData?.FullName}
                      isEditing={isEditMode}
                      onChange={val => handleFieldChange('FullName', val)}
                    />
                    <TenantField
                      label="Gender"
                      displayValue={data.Gender}
                      editValue={editedData?.Gender}
                      isEditing={isEditMode}
                      type="select"
                      options={['Male', 'Female', 'Other']}
                      onChange={val => handleFieldChange('Gender', val)}
                    />
                    <TenantField
                      label="Date of Birth"
                      displayValue={formatDateFull(data['Date Of Birth'])}
                      editValue={toInputDate(editedData?.['Date Of Birth'])}
                      isEditing={isEditMode}
                      type="date"
                      onChange={val => handleFieldChange('Date Of Birth', val)}
                    />
                    <TenantField
                      label="Mobile Number"
                      displayValue={data.MobileNumber}
                      editValue={editedData?.MobileNumber}
                      isEditing={isEditMode}
                      type="tel"
                      onChange={val => handleFieldChange('MobileNumber', val)}
                    />
                    <TenantField
                      label="Email Address"
                      displayValue={data.EmailId}
                      editValue={editedData?.EmailId}
                      isEditing={isEditMode}
                      type="email"
                      onChange={val => handleFieldChange('EmailId', val)}
                    />
                  </div>
                </div>

                <div className="tenant-detail-col">
                  <h4 className="tenant-detail-section-title">🆘 Emergency Contact</h4>
                  <div className="tenant-detail-grid">
                    <TenantField
                      label="Contact Name"
                      displayValue={data.EmergenctContactName}
                      editValue={editedData?.EmergenctContactName}
                      isEditing={isEditMode}
                      onChange={val => handleFieldChange('EmergenctContactName', val)}
                    />
                    <TenantField
                      label="Contact Number"
                      displayValue={data.EmergencyContactNumber}
                      editValue={editedData?.EmergencyContactNumber}
                      isEditing={isEditMode}
                      type="tel"
                      onChange={val => handleFieldChange('EmergencyContactNumber', val)}
                    />
                  </div>
                </div>
              </div>

              <div className="tenant-detail-row">
                <div className="tenant-detail-col">
                  <h4 className="tenant-detail-section-title">🏢 PG & Room Details</h4>
                  <div className="tenant-detail-grid">
                    <TenantField
                      label="PG Name"
                      displayValue={data.PGName}
                      editValue={editedData?.PGName}
                      isEditing={isEditMode}
                      onChange={val => handleFieldChange('PGName', val)}
                    />
                    <TenantField
                      label="Room Number"
                      displayValue={data.RoomNumber}
                      editValue={editedData?.RoomNumber}
                      isEditing={isEditMode}
                      onChange={val => handleFieldChange('RoomNumber', val)}
                    />
                    <TenantField
                      label="Bed Number"
                      displayValue={data.BedNumber}
                      editValue={editedData?.BedNumber}
                      isEditing={isEditMode}
                      onChange={val => handleFieldChange('BedNumber', val)}
                    />
                  </div>
                </div>

                <div className="tenant-detail-col">
                  <h4 className="tenant-detail-section-title">📅 Tenancy Dates</h4>
                  <div className="tenant-detail-grid">
                    <TenantField
                      label="Joining Date"
                      displayValue={formatDateFull(data.JoinDate)}
                      editValue={toInputDate(editedData?.JoinDate)}
                      isEditing={isEditMode}
                      type="date"
                      onChange={val => handleFieldChange('JoinDate', val)}
                    />
                    <TenantField
                      label="Notice Date"
                      displayValue={data.NoticeDate ? formatDateFull(data.NoticeDate) : '—'}
                      editValue={toInputDate(editedData?.NoticeDate)}
                      isEditing={isEditMode}
                      type="date"
                      onChange={val => handleFieldChange('NoticeDate', val)}
                    />
                    <TenantField
                      label="Vacate Date"
                      displayValue={data.VacateDate ? formatDateFull(data.VacateDate) : '—'}
                      editValue={toInputDate(editedData?.VacateDate)}
                      isEditing={isEditMode}
                      type="date"
                      onChange={val => handleFieldChange('VacateDate', val)}
                    />
                    <TenantField
                      label="Exit Date"
                      displayValue={data.ExitDate ? formatDateFull(data.ExitDate) : '—'}
                      editValue={toInputDate(editedData?.ExitDate)}
                      isEditing={isEditMode}
                      type="date"
                      onChange={val => handleFieldChange('ExitDate', val)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {!loading && data && isEditMode && (
          <div className="oc-modal-footer">
            <div className="oc-edit-footer-actions">
              <button className="oc-cancel-btn" onClick={handleCancelEdit} disabled={saving}>
                Cancel
              </button>
              <button className="oc-save-btn" onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Record Modal ─────────────────────────────────────────────────
const RecordModal = ({ title, sub, rows, columns, onClose }) => (
  <div className="oc-modal-overlay" onClick={onClose}>
    <div className="oc-modal" onClick={e => e.stopPropagation()}>
      <div className="oc-modal-head">
        <div>
          <h3 className="oc-modal-title">{title}</h3>
          <p className="oc-modal-sub">{sub}</p>
        </div>
        <button className="oc-modal-close" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
            <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      <div className="oc-modal-body">
        {rows.length === 0 ? (
          <p className="oc-modal-empty">No records found.</p>
        ) : (
          <table className="oc-modal-table">
            <thead>
              <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>{columns.map(c => <td key={c.key}>{row[c.key]}</td>)}</tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
)

// ─── Records Table Head ───────────────────────────────────────────
const RecordsHead = () => (
  <div className="oc-rec-head">
    <div className="oc-rc oc-rc-room">Room</div>
    <div className="oc-rc oc-rc-tenant">Tenant Name</div>
    <div className="oc-rc oc-rc-reg">Tenant Register</div>
    <div className="oc-rc oc-rc-police">Police Verification</div>
    <div className="oc-rc oc-rc-checkin">Check-In/Out</div>
    <div className="oc-rc oc-rc-visitors">Visitors</div>
    <div className="oc-rc oc-rc-agreement">Agreement</div>
    <div className="oc-rc oc-rc-download">Download All</div>
  </div>
)

// ─── Pending Requests Tab ─────────────────────────────────────────
const PendingRequests = ({ complianceData, onUpdateStatus, loading }) => {
  const [filter, setFilter] = useState('all')
  const [page, setPage] = useState(1)

  const pendingRequests = complianceData.filter(item => 
    item.ComplianceType !== 'Police Verification' && 
    item.Status === 'PENDING'
  )

  const getType = (complianceType) => {
    if (complianceType?.toLowerCase().includes('visitor')) return 'visitor'
    if (complianceType?.toLowerCase().includes('check') || complianceType?.toLowerCase().includes('in/out')) return 'checkin'
    return 'other'
  }

  const filtered = pendingRequests.filter(item => {
    if (filter === 'all') return true
    return getType(item.ComplianceType) === filter
  })

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const policeVerificationPending = complianceData.filter(item => 
    item.ComplianceType === 'Police Verification' && 
    item.Status === 'PENDING'
  )

  if (loading) {
    return (
      <div className="oc-root">
        <div className="oc-loading">
          <div className="oc-spinner"></div>
          <p>Loading compliance data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="oc-root">
      <div className="oc-section">
        <div className="oc-section-title-row">
          <h3 className="oc-section-title">Pending Requests</h3>
          <span className="oc-count-badge">{pendingRequests.length}</span>
        </div>

        <div className="oc-filter-pills">
          {[
            ['all', 'All', pendingRequests.length],
            ['visitor', 'Visitor', pendingRequests.filter(i => getType(i.ComplianceType) === 'visitor').length],
            ['checkin', 'Check-In / Check-Out', pendingRequests.filter(i => getType(i.ComplianceType) === 'checkin').length]
          ].map(([key, label, count]) => (
            <button 
              key={key} 
              className={`oc-filter-pill ${filter === key ? 'active' : ''}`}
              onClick={() => { setFilter(key); setPage(1) }}
            >
              {label}
              {count > 0 && <span className="oc-pill-count">{count}</span>}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="oc-table-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <p>No pending requests</p>
          </div>
        ) : (
          <div className="oc-table">
            <div className="oc-table-head">
              <div className="oc-col oc-col-room">Room</div>
              <div className="oc-col oc-col-tenant">Tenant</div>
              <div className="oc-col oc-col-type">Type</div>
              <div className="oc-col oc-col-details">Details</div>
              <div className="oc-col oc-col-date">Date Range</div>
              <div className="oc-col oc-col-actions">Actions</div>
            </div>
            {paged.map(item => (
              <div key={item.ComplianceId} className="oc-table-row">
                <div className="oc-col oc-col-room">{item.RoomNumber || '—'}</div>
                <div className="oc-col oc-col-tenant">
                  <strong>{item.tenantName}</strong>
                </div>
                <div className="oc-col oc-col-type">
                  <span className={`oc-type-badge oc-type-${getType(item.ComplianceType)}`}>
                    {getType(item.ComplianceType) === 'visitor'
                      ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
                    }
                    {getType(item.ComplianceType) === 'visitor' ? 'Visitor' : 'Check-In/Out'}
                  </span>
                </div>
                <div className="oc-col oc-col-details">
                  <span className="oc-detail-main">{item.ComplianceDetails || '—'}</span>
                </div>
                <div className="oc-col oc-col-date">{item.DateRange || formatDate(item.CreatedDate)}</div>
                <div className="oc-col oc-col-actions">
                  <button 
                    className="oc-btn oc-btn-approve" 
                    onClick={() => onUpdateStatus(item.ComplianceId, 'COMPLETED')}
                  >
                    Approve
                  </button>
                  <button 
                    className="oc-btn-text-reject" 
                    onClick={() => onUpdateStatus(item.ComplianceId, 'REJECTED')}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="oc-pagination">
            <span className="oc-page-info">Page {page} of {totalPages}</span>
            <div className="oc-page-btns">
              <button className="oc-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M13 4L7 10L13 16" stroke="currentColor" strokeWidth="2"/></svg>
              </button>
              <button className="oc-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M7 4L13 10L7 16" stroke="currentColor" strokeWidth="2"/></svg>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="oc-section oc-police-verification">
        <div className="oc-section-title-row">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <h3 className="oc-section-title">Police Verification Pending</h3>
          <span className="oc-count-badge oc-count-amber">{policeVerificationPending.length}</span>
        </div>
        
        {policeVerificationPending.length === 0 ? (
          <div className="oc-table-empty">
            <p>No police verification pending</p>
          </div>
        ) : (
          <div className="oc-table">
            <div className="oc-table-head">
              <div className="oc-col oc-col-room">Room</div>
              <div className="oc-col oc-col-tenant">Tenant Name</div>
              <div className="oc-col oc-col-pv-status">Status</div>
              <div className="oc-col oc-col-actions">Actions</div>
            </div>
            {policeVerificationPending.map(item => (
              <div key={item.ComplianceId} className="oc-table-row">
                <div className="oc-col oc-col-room">{item.RoomNo || '—'}</div>
                <div className="oc-col oc-col-tenant">
                  <strong>{item.TenantName || `Tenant #${item.TenantID}`}</strong>
                </div>
                <div className="oc-col oc-col-pv-status">
                  <span className={`oc-pv-badge oc-pv-${item.Status?.toLowerCase()}`}>
                    {item.Status?.charAt(0).toUpperCase() + item.Status?.slice(1).toLowerCase()}
                  </span>
                </div>
                <div className="oc-col oc-col-actions">
                  <button className="oc-btn-reminder">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    Send Reminder
                  </button>
                  <button 
                    className="oc-btn oc-btn-approve" 
                    onClick={() => onUpdateStatus(item.ComplianceId, 'COMPLETED')}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2"/></svg>
                    Mark Verified
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Tenant Record Row ────────────────────────────────────────────
const TenantRecordRow = ({ item, onViewAgreement, onViewTenantRegister }) => {
  const [modal, setModal] = useState(null)
  const ps = POLICE_CFG[item.PoliceVerificationStatus] || POLICE_CFG.PENDING
  const agreementStatus = item.AgreementID ? 'active' : 'NA'
  const agreementConfig = AGREEMENT_CFG[agreementStatus]

  return (
    <>
      <div className="oc-rec-row">
        <div className="oc-rc oc-rc-room">{item.RoomNumber || '—'}</div>
        <div className="oc-rc oc-rc-tenant"><strong>{item.TenantName || `Tenant #${item.TenantID}`}</strong></div>
        <div className="oc-rc oc-rc-reg">
          {item.AdmissionId || item.AdmissionID?(
            <button
              className="oc-link-btn"
              onClick={() => onViewTenantRegister(item)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              View
            </button>
          ):
          (
          <span className="oc-agreement-badge" style={{ background: agreementConfig.bg, color: agreementConfig.text, borderColor: agreementConfig.border }}>
            Not Available
          </span>
          )}
         
        </div>
        {/* <div className="oc-rc oc-rc-police">
          <span className="oc-police-label" style={{ color: ps.color }}>
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
              {item.Status === 'COMPLETED'
                ? <path d="M2 6L5 9L10 3" stroke={ps.dot} strokeWidth="2" strokeLinecap="round"/>
                : <circle cx="6" cy="6" r="5" stroke={ps.dot} strokeWidth="1.5"/>}
            </svg>
            {ps.label}
          </span>
          <button className="oc-icon-btn" title="Download">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
          </button>
        </div> */}

        <div className="oc-rc oc-rc-police">
          <span className="oc-police-label" style={{ color: ps.color }}>
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
              {item.PoliceVerificationStatus === 'Verified' || item.Status === 'COMPLETED'
                ? <path d="M2 6L5 9L10 3" stroke={ps.dot} strokeWidth="2" strokeLinecap="round" />
                : <circle cx="6" cy="6" r="5" stroke={ps.dot} strokeWidth="1.5" />}
            </svg>
            {item.PoliceVerificationStatus === 'Verified' || item.Status === 'COMPLETED' ? 'Verified' : 'Pending'}
          </span>
  
          {/* Download button - only show if document exists and status is Verified/Completed */}
          {item.PoliceVerificationStatus === 'Verified' || item.Status === 'COMPLETED' ? (
            <button
              className="oc-icon-btn"
              title="Download Acknowledgement"
              onClick={() => {
                // Extract the URL from PoliceVerificationDocuments
                // Format: "Acknowledgement|https://..."
                if (item.PoliceVerificationDocuments) {
                  const parts = item.PoliceVerificationDocuments.split('|');
                  if (parts.length === 2) {
                    const docUrl = parts[1]; // This is the URL part
                    // Open the URL in new tab or download
                    window.open(docUrl, '_blank');
                  }
                }
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
              </svg>
            </button>
          ) : (
            // Show disabled/placeholder when no document
            <button
              className="oc-icon-btn"
              title="No document available"
              disabled
              style={{ opacity: 0.3, cursor: 'not-allowed' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
              </svg>
            </button>
          )}
        </div>

        <div className="oc-rc oc-rc-checkin">
          <button className="oc-link-btn" onClick={() => setModal('checkin')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            0 records
          </button>
        </div>
        <div className="oc-rc oc-rc-visitors">
          <button className="oc-link-btn" onClick={() => setModal('visitors')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            0 visitors
          </button>
        </div>
        <div className="oc-rc oc-rc-agreement">
          <span className="oc-agreement-badge" style={{ background: agreementConfig.bg, color: agreementConfig.text, borderColor: agreementConfig.border }}>
            {agreementConfig.label}
          </span>
          {item.AgreementID && (
            <button 
              className="oc-icon-btn" 
              title="View agreement"
              onClick={() => onViewAgreement(item)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          )}
          
        </div>
        <div className="oc-rc oc-rc-download">
          <button className="oc-download-btn">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
            Download All
          </button>
        </div>
      </div>

      {modal === 'checkin' && (
        <RecordModal
          title="Check-In / Check-Out Records"
          sub={`${item.TenantName} · Room ${item.RoomNumber}`}
          rows={item.CheckIns || []}
          columns={[{ key: 'date', label: 'Date' }, { key: 'reason', label: 'Reason' }]}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'visitors' && (
        <RecordModal
          title="Visitor Records"
          sub={`${item.TenantName} · Room ${item.RoomNumber}`}
          rows={item.Visitors || []}
          columns={[{ key: 'name', label: 'Visitor Name' }, { key: 'relation', label: 'Relation' }, { key: 'date', label: 'Date' }]}
          onClose={() => setModal(null)}
        />
      )}
    </>
  )
}

// ─── Tenant Records Tab ───────────────────────────────────────────
const TenantRecords = ({ complianceData, loading, onViewAgreement, onViewTenantRegister, selectedPGId }) => {
  const [search, setSearch] = useState({ mobile: '', room: '', tenant: '' })
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [openMonths, setOpenMonths] = useState({})
  const [downloadingAll, setDownloadingAll] = useState(false)

  // Filter records - use TenantName instead of tenantName
  // const activeRecords = complianceData.filter(item => item.Status === 'COMPLETED')
  // const previousRecords = complianceData.filter(item => item.Status === 'REJECTED' || item.Status === 'PENDING')

    const activeRecords = complianceData
  const previousRecords = complianceData


  const applySearch = (records) => {
    return records.filter(item => {
      const ms = !search.mobile || (item.TenantName || '').toLowerCase().includes(search.mobile.toLowerCase())
      const rs = !search.room || (item.RoomNumber || '').includes(search.room)
      const ts = !search.tenant || (item.TenantName || '').toLowerCase().includes(search.tenant.toLowerCase())
      
      if (dateFrom && item.CreatedDate) {
        const itemDate = new Date(item.CreatedDate)
        const fromDate = new Date(dateFrom)
        if (itemDate < fromDate) return false
      }
      if (dateTo && item.CreatedDate) {
        const itemDate = new Date(item.CreatedDate)
        const toDate = new Date(dateTo)
        if (itemDate > toDate) return false
      }
      
      return ms && rs && ts
    })
  }

  const filteredActive = applySearch(activeRecords)
  const filteredPrevious = applySearch(previousRecords)

  const toggleMonth = (month) => setOpenMonths(p => ({ ...p, [month]: !p[month] }))

  const byMonth = {}
  filteredPrevious.forEach(item => {
    const month = new Date(item.CreatedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    if (!byMonth[month]) byMonth[month] = []
    byMonth[month].push(item)
  })
  const sortedMonths = Object.keys(byMonth).sort((a, b) => new Date(b) - new Date(a))

  // Handle Download All Tenant Register
  const handleDownloadAll = async () => {
    if (!selectedPGId) {
      alert('No PG selected')
      return
    }

    setDownloadingAll(true)
    try {
      const today = new Date()
      const fromDate = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
        .toISOString().split('T')[0]
      const toDate = today.toISOString().split('T')[0]

      const response = await getTenantRegisterByAdmission(selectedPGId, null, fromDate, toDate)
      
      console.log('Download All Response:', response)
      
      let tenantsData = []
      if (response.success) {
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          tenantsData = response.data
        } else if (response.tenants && Array.isArray(response.tenants) && response.tenants.length > 0) {
          tenantsData = response.tenants
        }
      }
      
      console.log('Tenants Data:', tenantsData)
      
      if (tenantsData.length === 0) {
        alert('No tenant data found to download')
        setDownloadingAll(false)
        return
      }

      const distinctMap = new Map()
      tenantsData.forEach(item => {
        const admissionId = item.AdmissionId || item.AdmissionID
        if (admissionId && !distinctMap.has(admissionId)) {
          distinctMap.set(admissionId, item)
        }
      })
      
      const distinctTenants = Array.from(distinctMap.values())

      const excelData = distinctTenants.map(item => ({
        'Tenant ID': item.TenantId || '—',
        'Admission ID': item.AdmissionId || item.AdmissionID || '—',
        'Full Name': item.FullName || '—',
        'Gender': item.Gender || '—',
        'Date of Birth': item['Date Of Birth'] || item.DateOfBirth || '—',
        'Mobile Number': item.MobileNumber || '—',
        'Email Address': item.EmailId || '—',
        'Emergency Contact Name': item.EmergenctContactName || '—',
        'Emergency Contact Number': item.EmergencyContactNumber || '—',
        'PG Name': item.PGName || '—',
        'Room Number': item.RoomNumber || '—',
        'Bed Number': item.BedNumber || '—',
        'Joining Date': item.JoinDate || '—',
        'Notice Date': item.NoticeDate || '—',
        'Vacate Date': item.VacateDate || '—',
        'Exit Date': item.ExitDate || '—'
      }))

      const pgName = distinctTenants[0]?.PGName || 'PG'
      const fileName = `All_Tenant_Register_${pgName}_${today.toISOString().split('T')[0]}`
      downloadExcel(excelData, fileName)
      
    } catch (error) {
      console.error('Error downloading all tenant register:', error)
      alert('Failed to download tenant register data: ' + (error.message || 'Unknown error'))
    } finally {
      setDownloadingAll(false)
    }
  }

  if (loading) {
    return (
      <div className="oc-root">
        <div className="oc-loading">
          <div className="oc-spinner"></div>
          <p>Loading compliance data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="oc-root">
      <div className="oc-filter-bar">
        <div className="oc-filter-group">
          <label className="oc-filter-label">Mobile</label>
          <input className="oc-filter-input" placeholder="Search mobile..." value={search.mobile}
            onChange={e => setSearch(p => ({ ...p, mobile: e.target.value }))} />
        </div>
        <div className="oc-filter-group">
          <label className="oc-filter-label">Room</label>
          <input className="oc-filter-input" placeholder="Room no." value={search.room}
            onChange={e => setSearch(p => ({ ...p, room: e.target.value }))} />
        </div>
        <div className="oc-filter-group">
          <label className="oc-filter-label">Tenant</label>
          <input className="oc-filter-input" placeholder="Name..." value={search.tenant}
            onChange={e => setSearch(p => ({ ...p, tenant: e.target.value }))} />
        </div>
        <div className="oc-filter-group">
          <label className="oc-filter-label">From</label>
          <div className="oc-date-wrap">
            <input type="date" className="oc-filter-input oc-date-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
        </div>
        <div className="oc-filter-group">
          <label className="oc-filter-label">To</label>
          <div className="oc-date-wrap">
            <input type="date" className="oc-filter-input oc-date-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
        </div>
        <div className="oc-filter-group">
          <label className="oc-filter-label">Status</label>
          <select className="oc-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="previous">Previous</option>
          </select>
        </div>

        <button 
          className="oc-download-all-btn"
          onClick={handleDownloadAll}
          disabled={downloadingAll}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
          </svg>
          {downloadingAll ? 'Downloading...' : 'Download All'}
        </button>
      </div>

      {(statusFilter === 'all' || statusFilter === 'active') && (
        <div className="oc-section">
          <div className="oc-section-title-row">
            <h3 className="oc-section-title">Active Tenants</h3>
            <span className="oc-count-badge">{filteredActive.length}</span>
          </div>
          {filteredActive.length === 0 ? (
            <div className="oc-table-empty">
              <p>No active tenants found</p>
            </div>
          ) : (
            <div className="oc-records-wrap">
              <RecordsHead />
              {filteredActive.map(item => (
                <TenantRecordRow 
                  key={item.TenantID || item.ComplianceId} 
                  item={item} 
                  onViewAgreement={onViewAgreement}
                  onViewTenantRegister={onViewTenantRegister}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* {(statusFilter === 'all' || statusFilter === 'previous') && filteredPrevious.length > 0 && (
        <div className="oc-section">
          <div className="oc-section-title-row">
            <h3 className="oc-section-title">Previous Tenants</h3>
            <span className="oc-count-badge oc-count-muted">{filteredPrevious.length}</span>
          </div>
          <div className="oc-accordion">
            {sortedMonths.map(month => {
              const items = byMonth[month]
              const isOpen = openMonths[month]
              return (
                <div key={month} className="oc-accordion-item">
                  <button className="oc-accordion-head" onClick={() => toggleMonth(month)}>
                    <div className="oc-accordion-title">
                      <span className="oc-accordion-month">{month}</span>
                      <span className="oc-accordion-count">{items.length} tenant{items.length !== 1 ? 's' : ''}</span>
                    </div>
                    <svg className={`oc-chevron ${isOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M3 5L7 9L11 5" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round"/>
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="oc-accordion-body">
                      <RecordsHead />
                      {items.map(item => (
                        <TenantRecordRow 
                          key={item.TenantID || item.ComplianceId} 
                          item={item} 
                          onViewAgreement={onViewAgreement}
                          onViewTenantRegister={onViewTenantRegister}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )} */}
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────
const OwnerCompliance = ({ selectedPG, selectedPGId }) => {
  const [activeTab, setActiveTab] = useState('pending')
  const [complianceData, setComplianceData] = useState([])
  const [tenantComplianceData, setTenantComplianceData] = useState([])
  const [loading, setLoading] = useState(false)
  const [tenantLoading, setTenantLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Agreement Modal State
  const [showAgreementModal, setShowAgreementModal] = useState(false)
  const [agreementTenantId, setAgreementTenantId] = useState(null)
  const [agreementStatus, setAgreementStatus] = useState('DRAFT')
  const [agreementStatusText, setAgreementStatusText] = useState('Draft')
  const [agreementStatusColor, setAgreementStatusColor] = useState('#F59E0B')
  
  // Tenant Register Modal State
  const [showTenantRegister, setShowTenantRegister] = useState(false)
  const [tenantRegisterData, setTenantRegisterData] = useState(null)
  const [tenantRegisterLoading, setTenantRegisterLoading] = useState(false)

  // Fetch compliance data (pending requests)
  useEffect(() => {
    if (selectedPGId) {
      fetchComplianceData()
      fetchTenantComplianceData()
    }
  }, [selectedPGId])

  const fetchComplianceData = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getAllComplianceByPGID(selectedPGId)
      if (response && response.length > 0) {
        setComplianceData(response)
      } else {
        setComplianceData([])
      }
    } catch (err) {
      console.error('Error fetching compliance data:', err)
      setError('Failed to load compliance data')
      setComplianceData([])
    } finally {
      setLoading(false)
    }
  }

  // New function to fetch tenant compliance records
  const fetchTenantComplianceData = async () => {
    setTenantLoading(true)
    try {
      const response = await getTenantComplianceRecords(selectedPGId)
      console.log('Tenant Compliance Records:', response)
       
      if (response && Array.isArray(response)) {
        setTenantComplianceData(response)
      } else {
        setTenantComplianceData([])
      }
    } catch (err) {
      console.error('Error fetching tenant compliance records:', err)
      setTenantComplianceData([])
    } finally {
      setTenantLoading(false)
    }
  }

  const handleUpdateStatus = async (complianceId, newStatus) => {
    console.log("Updating compliance status:", { complianceId, newStatus });
    
    try {
      const response = await updateComplianceStatus({
        ComplianceID: parseInt(complianceId),
        Status: newStatus
      })
      console.log("response from updateComplianceStatus:", response);
      
      if (response.Success === 1) {
        await fetchComplianceData()
        // Also refresh tenant compliance data after status update
        await fetchTenantComplianceData()
        alert('Status updated successfully!')
      } else {
        alert(response.Message || 'Failed to update status')
      }
    } catch (err) {
      console.error('Error updating status:', err)
      alert('Failed to update status')
    }
  }

  // Updated View Agreement Handler
  const handleViewAgreement = async (item) => {
    console.log("item", item);
    
    const tenantId = item.TenantID
    
    if (!tenantId) {
      alert('No Tenant ID found for this tenant')
      return
    }


    try {
      const response = await getAgreementJsonData(selectedPGId, tenantId);
      if (response.success && response.data) {
        const status = response.data.AgreementStatus || 'DRAFT';
        setAgreementStatus(status);

        // Set status display based on actual status
        if (status === 'TENANT_ACCEPTED') {
          setAgreementStatusText('Accepted');
          setAgreementStatusColor('#16A34A');
        } else if (status === 'REJECTED') {
          setAgreementStatusText('Rejected');
          setAgreementStatusColor('#DC2626');
        } else if (status === 'DRAFT') {
          setAgreementStatusText('Draft');
          setAgreementStatusColor('#F59E0B');
        } else {
          setAgreementStatusText(status);
          setAgreementStatusColor('#6B7280');
        }
      }
    } catch (error) {
      console.error('Error fetching agreement status:', error);
    }

        
    // Set agreement status based on item data
    // let status = 'DRAFT'
    // let statusText = 'Draft'
    // let statusColor = '#F59E0B'

    // if (item.AgreementStatus === 'TENANT_ACCEPTED') {
    //   status = 'TENANT_ACCEPTED'
    //   statusText = 'Accepted'
    //   statusColor = '#16A34A'
    // } else if (item.AgreementStatus === 'SENT') {
    //   status = 'SENT'
    //   statusText = 'Sent'
    //   statusColor = '#3B82F6'
    // }

    setAgreementTenantId(tenantId)
    // setAgreementStatus(status)
    // setAgreementStatusText(statusText)
    // setAgreementStatusColor(statusColor)
    setShowAgreementModal(true)
  }

  // Updated View Tenant Register Handler
  const handleViewTenantRegister = async (item) => {
    const admissionId = item.AdmissionId || item.AdmissionID
    
    if (!admissionId) {
      alert('No Admission ID found for this tenant')
      return
    }

    setShowTenantRegister(true)
    setTenantRegisterLoading(true)
    setTenantRegisterData(null)

    try {
      const today = new Date()
      const fromDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())
        .toISOString().split('T')[0]
      const toDate = today.toISOString().split('T')[0]
      
      const pgId = selectedPGId

      console.log('Fetching tenant register with:', { pgId, admissionId, fromDate, toDate })
      
      const response = await getTenantRegisterByAdmission(pgId, admissionId, fromDate, toDate)
      console.log('Tenant Register Response:', response)
      
      if (response.success) {
        setTenantRegisterData(response.tenants || response.data || response)
      } else {
        alert(response.message || 'Failed to fetch tenant details')
        setShowTenantRegister(false)
      }
    } catch (err) {
      console.error('Error fetching tenant register:', err)
      alert('Failed to fetch tenant details')
      setShowTenantRegister(false)
    } finally {
      setTenantRegisterLoading(false)
    }
  }

  if (error) {
    return (
      <div className="ocomp-root">
        <div className="oc-error">
          <p>{error}</p>
          <button onClick={fetchComplianceData} className="oc-retry-btn">Retry</button>
        </div>
      </div>
    )
  }

  return (
    <div className="ocomp-root">
      <div className="ocomp-tabs">
        <button className={`ocomp-tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
          Pending Requests
        </button>
        <button className={`ocomp-tab ${activeTab === 'records' ? 'active' : ''}`} onClick={() => setActiveTab('records')}>
          Tenant Compliance Records
        </button>
      </div>
      
      {activeTab === 'pending' && (
        <PendingRequests 
          complianceData={complianceData}
          onUpdateStatus={handleUpdateStatus}
          loading={loading}
        />
      )}
      
      {activeTab === 'records' && (
        <TenantRecords 
          complianceData={tenantComplianceData}
          loading={tenantLoading}
          onViewAgreement={handleViewAgreement}
          onViewTenantRegister={handleViewTenantRegister}
          selectedPGId={selectedPGId}
        />
      )}

      {/* Tenant Agreement Modal */}
      {showAgreementModal && (
        <TenantAgreementModalView
          isOpen={showAgreementModal}
          onClose={() => {
            setShowAgreementModal(false)
            setAgreementTenantId(null)
          }}
          pgId={selectedPGId}
          tenantId={agreementTenantId}
          agreementStatus={agreementStatus}
          statusText={agreementStatusText}
          statusColor={agreementStatusColor}
          showSaveToCloud={true}
          onUploadSuccess={(data) => {
            console.log('Upload successful:', data)
            // Refresh tenant compliance data after upload
            fetchTenantComplianceData()
          }}
        />
      )}

      {showTenantRegister && (
        <TenantRegisterModal
          tenantData={tenantRegisterData}
          loading={tenantRegisterLoading}
          onClose={() => {
            setShowTenantRegister(false)
            setTenantRegisterData(null)
          }}
        />
      )}
    </div>
  )
}

export default OwnerCompliance

// import '../scss/components/_ownercompliance.scss'
// import React, { useState, useEffect } from 'react'
// import { getAllComplianceByPGID, updateComplianceStatus, getTenantRegisterByAdmission } from '../apis/services/pgApi'
// import ViewAgreementModal from './RentAgreement'
// import * as XLSX from 'xlsx'

// // ─── Constants ────────────────────────────────────────────────────
// const POLICE_CFG = {
//   COMPLETED: { label: 'Completed', color: '#16A34A', dot: '#22C55E' },
//   PENDING: { label: 'Pending', color: '#D97706', dot: '#F59E0B' },
//   REJECTED: { label: 'Rejected', color: '#DC2626', dot: '#EF4444' },
//   IN_PROGRESS: { label: 'In Progress', color: '#3B82F6', dot: '#60A5FA' },
// }

// const AGREEMENT_CFG = {
//   active: { label: 'Active', bg: '#F0FDF4', text: '#166534', border: '#86EFAC' },
//   expired: { label: 'Expired', bg: '#FFF1F2', text: '#991B1B', border: '#FECACA' },
//   expiring_soon: { label: 'Expiring Soon', bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
// }

// const PAGE_SIZE = 5

// // ─── Helper Functions ─────────────────────────────────────────────────
// const formatDate = (dateString) => {
//   if (!dateString) return '—'
//   const date = new Date(dateString)
//   return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
// }

// const formatDateFull = (dateString) => {
//   if (!dateString) return '—'
//   const date = new Date(dateString)
//   return date.toLocaleDateString('en-US', { 
//     weekday: 'long',
//     year: 'numeric', 
//     month: 'long', 
//     day: 'numeric' 
//   })
// }

// // Converts any parseable date value into the yyyy-mm-dd format required by <input type="date">
// const toInputDate = (dateString) => {
//   if (!dateString) return ''
//   const date = new Date(dateString)
//   if (isNaN(date.getTime())) return ''
//   return date.toISOString().split('T')[0]
// }

// // ─── Excel Download Helper ────────────────────────────────────────────
// const downloadExcel = (data, fileName) => {
//   if (!data || data.length === 0) {
//     alert('No data to download')
//     return
//   }

//   try {
//     const ws = XLSX.utils.json_to_sheet(data)
    
//     const colWidths = []
//     const headers = Object.keys(data[0])
//     headers.forEach((key, index) => {
//       let maxLength = key.length
//       data.forEach(row => {
//         const value = row[key] !== undefined && row[key] !== null ? String(row[key]) : ''
//         if (value.length > maxLength) maxLength = value.length
//       })
//       colWidths.push({ wch: Math.min(Math.max(maxLength + 4, 12), 40) })
//     })
//     ws['!cols'] = colWidths

//     const wb = XLSX.utils.book_new()
//     XLSX.utils.book_append_sheet(wb, ws, 'Tenant Register')
//     XLSX.writeFile(wb, `${fileName}.xlsx`)
//   } catch (error) {
//     console.error('Error downloading Excel:', error)
//     alert('Failed to download Excel file')
//   }
// }

// // ─── Tenant Field (view / edit toggle) ─────────────────────────────
// const TenantField = ({ label, displayValue, editValue, isEditing, editable = true, type = 'text', options, onChange }) => (
//   <div className="tenant-detail-item">
//     <span className="tenant-detail-label">{label}</span>
//     {isEditing && editable ? (
//       type === 'select' ? (
//         <select
//           className="tenant-detail-input"
//           value={editValue || ''}
//           onChange={e => onChange(e.target.value)}
//         >
//           {options.map(opt => (
//             <option key={opt} value={opt}>{opt}</option>
//           ))}
//         </select>
//       ) : (
//         <input
//           className="tenant-detail-input"
//           type={type}
//           value={editValue || ''}
//           onChange={e => onChange(e.target.value)}
//         />
//       )
//     ) : (
//       <span className="tenant-detail-value">{displayValue || '—'}</span>
//     )}
//   </div>
// )

// // ─── Tenant Register Modal ─────────────────────────────────────────
// const TenantRegisterModal = ({ tenantData, onClose, loading }) => {
//   // savedOverride holds the last successfully-saved edits so the modal reflects
//   // the update immediately, without needing the parent to refetch.
//   const [savedOverride, setSavedOverride] = useState(null)
//   const [isEditMode, setIsEditMode] = useState(false)
//   const [editedData, setEditedData] = useState(null)
//   const [saving, setSaving] = useState(false)

//   const rawData = Array.isArray(tenantData) ? tenantData[0] : tenantData
//   const data = savedOverride || rawData

//   // Reset local edit state whenever a new tenant is loaded into the modal
//   useEffect(() => {
//     setSavedOverride(null)
//     setIsEditMode(false)
//     setEditedData(rawData ? { ...rawData } : null)
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [tenantData])

//   if (!tenantData) return null

//   const handleDownloadSingle = () => {
//     if (!data) {
//       alert('No data to download')
//       return
//     }

//     const excelData = {
//       'Tenant ID': data.TenantId || '—',
//       'Full Name': data.FullName || '—',
//       'Gender': data.Gender || '—',
//       'Date of Birth': data['Date Of Birth'] || '—',
//       'Mobile Number': data.MobileNumber || '—',
//       'Email Address': data.EmailId || '—',
//       'Emergency Contact Name': data.EmergenctContactName || '—',
//       'Emergency Contact Number': data.EmergencyContactNumber || '—',
//       'PG Name': data.PGName || '—',
//       'Room Number': data.RoomNumber || '—',
//       'Bed Number': data.BedNumber || '—',
//       'Joining Date': data.JoinDate || '—',
//       'Notice Date': data.NoticeDate || '—',
//       'Vacate Date': data.VacateDate || '—',
//       'Exit Date': data.ExitDate || '—'
//     }

//     const fileName = `Tenant_Register_${data.FullName || 'Tenant'}_${data.RoomNumber || ''}`
//     downloadExcel([excelData], fileName)
//   }

//   const handleEditClick = () => {
//     setEditedData({ ...data })
//     setIsEditMode(true)
//   }

//   const handleCancelEdit = () => {
//     setEditedData({ ...data })
//     setIsEditMode(false)
//   }

//   const handleFieldChange = (field, value) => {
//     setEditedData(prev => ({ ...prev, [field]: value }))
//   }

//   const handleSaveEdit = async () => {
//     setSaving(true)
//     try {
//       // TODO: wire this up to the real update-tenant API once it's available, e.g.
//       // const response = await updateTenantRegister(data.TenantId, editedData)
//       // if (!response.success) {
//       //   alert(response.message || 'Failed to update tenant details')
//       //   return
//       // }

//       setSavedOverride({ ...editedData })
//       setIsEditMode(false)
//     } catch (err) {
//       console.error('Error updating tenant register:', err)
//       alert('Failed to update tenant details')
//     } finally {
//       setSaving(false)
//     }
//   }

//   return (
//     <div className="oc-modal-overlay" onClick={isEditMode ? undefined : onClose}>
//       <div className="oc-modal oc-modal-large" onClick={e => e.stopPropagation()}>
//         <div className="oc-modal-head">
//           <div>
//             <h3 className="oc-modal-title">📋 Tenant Register Details</h3>
//             <p className="oc-modal-sub">{isEditMode ? 'Editing tenant information' : 'Complete tenant information'}</p>
//           </div>
//           <div className="oc-modal-head-actions">
//             {!loading && data && !isEditMode && (
//               <>
//                 <button
//                   className="oc-edit-btn"
//                   onClick={handleEditClick}
//                   title="Edit tenant details"
//                 >
//                   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                     <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
//                     <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
//                   </svg>
//                   Edit
//                 </button>
//                 <button 
//                   className="oc-download-single-btn"
//                   onClick={handleDownloadSingle}
//                   title="Download this tenant's register"
//                 >
//                   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                     <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
//                   </svg>
//                   Download
//                 </button>
//               </>
//             )}
//             <button className="oc-modal-close" onClick={onClose} disabled={isEditMode && saving}>
//               <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
//                 <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
//               </svg>
//             </button>
//           </div>
//         </div>
//         <div className="oc-modal-body oc-modal-body-no-scroll">
//           {loading ? (
//             <div className="oc-loading">
//               <div className="oc-spinner"></div>
//               <p>Loading tenant details...</p>
//             </div>
//           ) : !data ? (
//             <div className="oc-modal-empty">
//               <p>No tenant data found</p>
//             </div>
//           ) : (
//             <div className="tenant-register-details">
//               <div className="tenant-detail-row">
//                 <div className="tenant-detail-col">
//                   <h4 className="tenant-detail-section-title">👤 Personal Information</h4>
//                   <div className="tenant-detail-grid">
//                     <TenantField
//                       label="Tenant ID"
//                       displayValue={data.TenantId}
//                       editable={false}
//                       isEditing={isEditMode}
//                     />
//                     <TenantField
//                       label="Full Name"
//                       displayValue={data.FullName}
//                       editValue={editedData?.FullName}
//                       isEditing={isEditMode}
//                       onChange={val => handleFieldChange('FullName', val)}
//                     />
//                     <TenantField
//                       label="Gender"
//                       displayValue={data.Gender}
//                       editValue={editedData?.Gender}
//                       isEditing={isEditMode}
//                       type="select"
//                       options={['Male', 'Female', 'Other']}
//                       onChange={val => handleFieldChange('Gender', val)}
//                     />
//                     <TenantField
//                       label="Date of Birth"
//                       displayValue={formatDateFull(data['Date Of Birth'])}
//                       editValue={toInputDate(editedData?.['Date Of Birth'])}
//                       isEditing={isEditMode}
//                       type="date"
//                       onChange={val => handleFieldChange('Date Of Birth', val)}
//                     />
//                     <TenantField
//                       label="Mobile Number"
//                       displayValue={data.MobileNumber}
//                       editValue={editedData?.MobileNumber}
//                       isEditing={isEditMode}
//                       type="tel"
//                       onChange={val => handleFieldChange('MobileNumber', val)}
//                     />
//                     <TenantField
//                       label="Email Address"
//                       displayValue={data.EmailId}
//                       editValue={editedData?.EmailId}
//                       isEditing={isEditMode}
//                       type="email"
//                       onChange={val => handleFieldChange('EmailId', val)}
//                     />
//                   </div>
//                 </div>

//                 <div className="tenant-detail-col">
//                   <h4 className="tenant-detail-section-title">🆘 Emergency Contact</h4>
//                   <div className="tenant-detail-grid">
//                     <TenantField
//                       label="Contact Name"
//                       displayValue={data.EmergenctContactName}
//                       editValue={editedData?.EmergenctContactName}
//                       isEditing={isEditMode}
//                       onChange={val => handleFieldChange('EmergenctContactName', val)}
//                     />
//                     <TenantField
//                       label="Contact Number"
//                       displayValue={data.EmergencyContactNumber}
//                       editValue={editedData?.EmergencyContactNumber}
//                       isEditing={isEditMode}
//                       type="tel"
//                       onChange={val => handleFieldChange('EmergencyContactNumber', val)}
//                     />
//                   </div>
//                 </div>
//               </div>

//               <div className="tenant-detail-row">
//                 <div className="tenant-detail-col">
//                   <h4 className="tenant-detail-section-title">🏢 PG & Room Details</h4>
//                   <div className="tenant-detail-grid">
//                     <TenantField
//                       label="PG Name"
//                       displayValue={data.PGName}
//                       editValue={editedData?.PGName}
//                       isEditing={isEditMode}
//                       onChange={val => handleFieldChange('PGName', val)}
//                     />
//                     {/* for read only use below */}
//                     {/* <TenantField
//                       label="PG Name"
//                       displayValue={data.PGName}
//                       editable={false}
//                       isEditing={isEditMode}
//                     /> */}
//                     <TenantField
//                       label="Room Number"
//                       displayValue={data.RoomNumber}
//                       editValue={editedData?.RoomNumber}
//                       isEditing={isEditMode}
//                       onChange={val => handleFieldChange('RoomNumber', val)}
//                     />
//                     <TenantField
//                       label="Bed Number"
//                       displayValue={data.BedNumber}
//                       editValue={editedData?.BedNumber}
//                       isEditing={isEditMode}
//                       onChange={val => handleFieldChange('BedNumber', val)}
//                     />
//                   </div>
//                 </div>

//                 <div className="tenant-detail-col">
//                   <h4 className="tenant-detail-section-title">📅 Tenancy Dates</h4>
//                   <div className="tenant-detail-grid">
//                     <TenantField
//                       label="Joining Date"
//                       displayValue={formatDateFull(data.JoinDate)}
//                       editValue={toInputDate(editedData?.JoinDate)}
//                       isEditing={isEditMode}
//                       type="date"
//                       onChange={val => handleFieldChange('JoinDate', val)}
//                     />
//                     <TenantField
//                       label="Notice Date"
//                       displayValue={data.NoticeDate ? formatDateFull(data.NoticeDate) : '—'}
//                       editValue={toInputDate(editedData?.NoticeDate)}
//                       isEditing={isEditMode}
//                       type="date"
//                       onChange={val => handleFieldChange('NoticeDate', val)}
//                     />
//                     <TenantField
//                       label="Vacate Date"
//                       displayValue={data.VacateDate ? formatDateFull(data.VacateDate) : '—'}
//                       editValue={toInputDate(editedData?.VacateDate)}
//                       isEditing={isEditMode}
//                       type="date"
//                       onChange={val => handleFieldChange('VacateDate', val)}
//                     />
//                     <TenantField
//                       label="Exit Date"
//                       displayValue={data.ExitDate ? formatDateFull(data.ExitDate) : '—'}
//                       editValue={toInputDate(editedData?.ExitDate)}
//                       isEditing={isEditMode}
//                       type="date"
//                       onChange={val => handleFieldChange('ExitDate', val)}
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}
//         </div>
//         {!loading && data && isEditMode && (
//           <div className="oc-modal-footer">
//             <div className="oc-edit-footer-actions">
//               <button className="oc-cancel-btn" onClick={handleCancelEdit} disabled={saving}>
//                 Cancel
//               </button>
//               <button className="oc-save-btn" onClick={handleSaveEdit} disabled={saving}>
//                 {saving ? 'Saving...' : 'Save Changes'}
//               </button>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

// // ─── Record Modal ─────────────────────────────────────────────────
// const RecordModal = ({ title, sub, rows, columns, onClose }) => (
//   <div className="oc-modal-overlay" onClick={onClose}>
//     <div className="oc-modal" onClick={e => e.stopPropagation()}>
//       <div className="oc-modal-head">
//         <div>
//           <h3 className="oc-modal-title">{title}</h3>
//           <p className="oc-modal-sub">{sub}</p>
//         </div>
//         <button className="oc-modal-close" onClick={onClose}>
//           <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
//             <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
//           </svg>
//         </button>
//       </div>
//       <div className="oc-modal-body">
//         {rows.length === 0 ? (
//           <p className="oc-modal-empty">No records found.</p>
//         ) : (
//           <table className="oc-modal-table">
//             <thead>
//               <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
//             </thead>
//             <tbody>
//               {rows.map((row, i) => (
//                 <tr key={i}>{columns.map(c => <td key={c.key}>{row[c.key]}</td>)}</tr>
//               ))}
//             </tbody>
//           </table>
//         )}
//       </div>
//     </div>
//   </div>
// )

// // ─── Records Table Head ───────────────────────────────────────────
// const RecordsHead = () => (
//   <div className="oc-rec-head">
//     <div className="oc-rc oc-rc-room">Room</div>
//     <div className="oc-rc oc-rc-tenant">Tenant Name</div>
//     <div className="oc-rc oc-rc-reg">Tenant Register</div>
//     <div className="oc-rc oc-rc-police">Police Verification</div>
//     <div className="oc-rc oc-rc-checkin">Check-In/Out</div>
//     <div className="oc-rc oc-rc-visitors">Visitors</div>
//     <div className="oc-rc oc-rc-agreement">Agreement</div>
//     <div className="oc-rc oc-rc-download">Download All</div>
//   </div>
// )

// // ─── Pending Requests Tab ─────────────────────────────────────────
// const PendingRequests = ({ complianceData, onUpdateStatus, loading }) => {
//   const [filter, setFilter] = useState('all')
//   const [page, setPage] = useState(1)

//   const pendingRequests = complianceData.filter(item => 
//     item.ComplianceType !== 'Police Verification' && 
//     item.Status === 'PENDING'
//   )

//   const getType = (complianceType) => {
//     if (complianceType?.toLowerCase().includes('visitor')) return 'visitor'
//     if (complianceType?.toLowerCase().includes('check') || complianceType?.toLowerCase().includes('in/out')) return 'checkin'
//     return 'other'
//   }

//   const filtered = pendingRequests.filter(item => {
//     if (filter === 'all') return true
//     return getType(item.ComplianceType) === filter
//   })

//   const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
//   const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

//   const policeVerificationPending = complianceData.filter(item => 
//     item.ComplianceType === 'Police Verification' && 
//     item.Status === 'PENDING'
//   )

//   if (loading) {
//     return (
//       <div className="oc-root">
//         <div className="oc-loading">
//           <div className="oc-spinner"></div>
//           <p>Loading compliance data...</p>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="oc-root">
//       <div className="oc-section">
//         <div className="oc-section-title-row">
//           <h3 className="oc-section-title">Pending Requests</h3>
//           <span className="oc-count-badge">{pendingRequests.length}</span>
//         </div>

//         <div className="oc-filter-pills">
//           {[
//             ['all', 'All', pendingRequests.length],
//             ['visitor', 'Visitor', pendingRequests.filter(i => getType(i.ComplianceType) === 'visitor').length],
//             ['checkin', 'Check-In / Check-Out', pendingRequests.filter(i => getType(i.ComplianceType) === 'checkin').length]
//           ].map(([key, label, count]) => (
//             <button 
//               key={key} 
//               className={`oc-filter-pill ${filter === key ? 'active' : ''}`}
//               onClick={() => { setFilter(key); setPage(1) }}
//             >
//               {label}
//               {count > 0 && <span className="oc-pill-count">{count}</span>}
//             </button>
//           ))}
//         </div>

//         {filtered.length === 0 ? (
//           <div className="oc-table-empty">
//             <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
//               <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
//             </svg>
//             <p>No pending requests</p>
//           </div>
//         ) : (
//           <div className="oc-table">
//             <div className="oc-table-head">
//               <div className="oc-col oc-col-room">Room</div>
//               <div className="oc-col oc-col-tenant">Tenant</div>
//               <div className="oc-col oc-col-type">Type</div>
//               <div className="oc-col oc-col-details">Details</div>
//               <div className="oc-col oc-col-date">Date Range</div>
//               <div className="oc-col oc-col-actions">Actions</div>
//             </div>
//             {paged.map(item => (
//               <div key={item.ComplianceId} className="oc-table-row">
//                 <div className="oc-col oc-col-room">{item.RoomNumber || '—'}</div>
//                 <div className="oc-col oc-col-tenant">
//                   <strong>{item.tenantName}</strong>
//                 </div>
//                 <div className="oc-col oc-col-type">
//                   <span className={`oc-type-badge oc-type-${getType(item.ComplianceType)}`}>
//                     {getType(item.ComplianceType) === 'visitor'
//                       ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
//                       : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
//                     }
//                     {getType(item.ComplianceType) === 'visitor' ? 'Visitor' : 'Check-In/Out'}
//                   </span>
//                 </div>
//                 <div className="oc-col oc-col-details">
//                   <span className="oc-detail-main">{item.ComplianceDetails || '—'}</span>
//                 </div>
//                 <div className="oc-col oc-col-date">{item.DateRange || formatDate(item.CreatedDate)}</div>
//                 <div className="oc-col oc-col-actions">
//                   <button 
//                     className="oc-btn oc-btn-approve" 
//                     onClick={() => onUpdateStatus(item.ComplianceId, 'COMPLETED')}
//                   >
//                     Approve
//                   </button>
//                   <button 
//                     className="oc-btn-text-reject" 
//                     onClick={() => onUpdateStatus(item.ComplianceId, 'REJECTED')}
//                   >
//                     Reject
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}

//         {totalPages > 1 && (
//           <div className="oc-pagination">
//             <span className="oc-page-info">Page {page} of {totalPages}</span>
//             <div className="oc-page-btns">
//               <button className="oc-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
//                 <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M13 4L7 10L13 16" stroke="currentColor" strokeWidth="2"/></svg>
//               </button>
//               <button className="oc-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
//                 <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M7 4L13 10L7 16" stroke="currentColor" strokeWidth="2"/></svg>
//               </button>
//             </div>
//           </div>
//         )}
//       </div>

//       <div className="oc-section oc-police-verification">
//         <div className="oc-section-title-row">
//           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
//           <h3 className="oc-section-title">Police Verification Pending</h3>
//           <span className="oc-count-badge oc-count-amber">{policeVerificationPending.length}</span>
//         </div>
        
//         {policeVerificationPending.length === 0 ? (
//           <div className="oc-table-empty">
//             <p>No police verification pending</p>
//           </div>
//         ) : (
//           <div className="oc-table">
//             <div className="oc-table-head">
//               <div className="oc-col oc-col-room">Room</div>
//               <div className="oc-col oc-col-tenant">Tenant Name</div>
//               <div className="oc-col oc-col-pv-status">Status</div>
//               <div className="oc-col oc-col-actions">Actions</div>
//             </div>
//             {policeVerificationPending.map(item => (
//               <div key={item.ComplianceId} className="oc-table-row">
//                 <div className="oc-col oc-col-room">{item.RoomNo || '—'}</div>
//                 <div className="oc-col oc-col-tenant">
//                   <strong>{item.TenantName || `Tenant #${item.TenantID}`}</strong>
//                 </div>
//                 <div className="oc-col oc-col-pv-status">
//                   <span className={`oc-pv-badge oc-pv-${item.Status?.toLowerCase()}`}>
//                     {item.Status?.charAt(0).toUpperCase() + item.Status?.slice(1).toLowerCase()}
//                   </span>
//                 </div>
//                 <div className="oc-col oc-col-actions">
//                   <button className="oc-btn-reminder">
//                     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
//                     Send Reminder
//                   </button>
//                   <button 
//                     className="oc-btn oc-btn-approve" 
//                     onClick={() => onUpdateStatus(item.ComplianceId, 'COMPLETED')}
//                   >
//                     <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2"/></svg>
//                     Mark Verified
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

// // ─── Tenant Record Row ────────────────────────────────────────────
// const TenantRecordRow = ({ item, onViewAgreement, onViewTenantRegister }) => {
//   const [modal, setModal] = useState(null)
//   const ps = POLICE_CFG[item.Status] || POLICE_CFG.PENDING
//   const agreementStatus = item.Status === 'COMPLETED' ? 'active' : 'expired'
//   const agreementConfig = AGREEMENT_CFG[agreementStatus]

//   return (
//     <>
//       <div className="oc-rec-row">
//         <div className="oc-rc oc-rc-room">{item.RoomNumber || '—'}</div>
//         <div className="oc-rc oc-rc-tenant"><strong>{item.tenantName || `Tenant #${item.tenantId}`}</strong></div>
//         <div className="oc-rc oc-rc-reg">
//           <button 
//             className="oc-link-btn"
//             onClick={() => onViewTenantRegister(item)}
//           >
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2">
//               <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
//               <circle cx="12" cy="12" r="3"/>
//             </svg>
//             View
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-police">
//           <span className="oc-police-label" style={{ color: ps.color }}>
//             <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
//               {item.Status === 'COMPLETED'
//                 ? <path d="M2 6L5 9L10 3" stroke={ps.dot} strokeWidth="2" strokeLinecap="round"/>
//                 : <circle cx="6" cy="6" r="5" stroke={ps.dot} strokeWidth="1.5"/>}
//             </svg>
//             {ps.label}
//           </span>
//           <button className="oc-icon-btn" title="Download">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-checkin">
//           <button className="oc-link-btn" onClick={() => setModal('checkin')}>
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//             0 records
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-visitors">
//           <button className="oc-link-btn" onClick={() => setModal('visitors')}>
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//             0 visitors
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-agreement">
//           <span className="oc-agreement-badge" style={{ background: agreementConfig.bg, color: agreementConfig.text, borderColor: agreementConfig.border }}>
//             {agreementConfig.label}
//           </span>
//           <button 
//             className="oc-icon-btn" 
//             title="View agreement"
//             onClick={() => onViewAgreement(item)}
//           >
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-download">
//           <button className="oc-download-btn">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
//             Download All
//           </button>
//         </div>
//       </div>

//       {modal === 'checkin' && (
//         <RecordModal
//           title="Check-In / Check-Out Records"
//           sub={`${item.tenantName} · Room ${item.RoomNumber}`}
//           rows={item.CheckIns || []}
//           columns={[{ key: 'date', label: 'Date' }, { key: 'reason', label: 'Reason' }]}
//           onClose={() => setModal(null)}
//         />
//       )}
//       {modal === 'visitors' && (
//         <RecordModal
//           title="Visitor Records"
//           sub={`${item.tenantName} · Room ${item.RoomNumber}`}
//           rows={item.Visitors || []}
//           columns={[{ key: 'name', label: 'Visitor Name' }, { key: 'relation', label: 'Relation' }, { key: 'date', label: 'Date' }]}
//           onClose={() => setModal(null)}
//         />
//       )}
//     </>
//   )
// }

// // ─── Tenant Records Tab ───────────────────────────────────────────


// const TenantRecords = ({ complianceData, loading, onViewAgreement, onViewTenantRegister, selectedPGId }) => {
//   const [search, setSearch] = useState({ mobile: '', room: '', tenant: '' })
//   const [dateFrom, setDateFrom] = useState('')
//   const [dateTo, setDateTo] = useState('')
//   const [statusFilter, setStatusFilter] = useState('all')
//   const [openMonths, setOpenMonths] = useState({})
//   const [downloadingAll, setDownloadingAll] = useState(false)

//   const activeRecords = complianceData.filter(item => item.Status === 'COMPLETED')
//   const previousRecords = complianceData.filter(item => item.Status === 'REJECTED')

//   const applySearch = (records) => {
//     return records.filter(item => {
//       const ms = !search.mobile || (item.TenantName || '').toLowerCase().includes(search.mobile.toLowerCase())
//       const rs = !search.room || (item.RoomNo || '').includes(search.room)
//       const ts = !search.tenant || (item.TenantName || '').toLowerCase().includes(search.tenant.toLowerCase())
      
//       if (dateFrom && item.CreatedDate) {
//         const itemDate = new Date(item.CreatedDate)
//         const fromDate = new Date(dateFrom)
//         if (itemDate < fromDate) return false
//       }
//       if (dateTo && item.CreatedDate) {
//         const itemDate = new Date(item.CreatedDate)
//         const toDate = new Date(dateTo)
//         if (itemDate > toDate) return false
//       }
      
//       return ms && rs && ts
//     })
//   }

//   const filteredActive = applySearch(activeRecords)
//   const filteredPrevious = applySearch(previousRecords)

//   const toggleMonth = (month) => setOpenMonths(p => ({ ...p, [month]: !p[month] }))

//   const byMonth = {}
//   filteredPrevious.forEach(item => {
//     const month = new Date(item.CreatedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
//     if (!byMonth[month]) byMonth[month] = []
//     byMonth[month].push(item)
//   })
//   const sortedMonths = Object.keys(byMonth).sort((a, b) => new Date(b) - new Date(a))

//   // Handle Download All Tenant Register
//   const handleDownloadAll = async () => {
//     if (!selectedPGId) {
//       alert('No PG selected')
//       return
//     }

//     setDownloadingAll(true)
//     try {
//       const today = new Date()
//       const fromDate = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
//         .toISOString().split('T')[0]
//       const toDate = today.toISOString().split('T')[0]

//       const response = await getTenantRegisterByAdmission(selectedPGId, null, fromDate, toDate)
      
//       console.log('Download All Response:', response)
      
//       // ✅ Check both 'data' and 'tenants' keys in response
//       let tenantsData = []
//       if (response.success) {
//         // Check if data exists in response.data or response.tenants
//         if (response.data && Array.isArray(response.data) && response.data.length > 0) {
//           tenantsData = response.data
//         } else if (response.tenants && Array.isArray(response.tenants) && response.tenants.length > 0) {
//           tenantsData = response.tenants
//         }
//       }
      
//       console.log('Tenants Data:', tenantsData)
      
//       if (tenantsData.length === 0) {
//         alert('No tenant data found to download')
//         setDownloadingAll(false)
//         return
//       }

//       // ✅ Get distinct tenants by AdmissionId
//       const distinctMap = new Map()
//       tenantsData.forEach(item => {
//         const admissionId = item.AdmissionId || item.AdmissionID
//         if (admissionId && !distinctMap.has(admissionId)) {
//           distinctMap.set(admissionId, item)
//         }
//       })
      
//       const distinctTenants = Array.from(distinctMap.values())
//       console.log('Distinct Tenants:', distinctTenants)

//       // Format data for Excel
//       const excelData = distinctTenants.map(item => ({
//         'Tenant ID': item.TenantId || '—',
//         'Admission ID': item.AdmissionId || item.AdmissionID || '—',
//         'Full Name': item.FullName || '—',
//         'Gender': item.Gender || '—',
//         'Date of Birth': item['Date Of Birth'] || item.DateOfBirth || '—',
//         'Mobile Number': item.MobileNumber || '—',
//         'Email Address': item.EmailId || '—',
//         'Emergency Contact Name': item.EmergenctContactName || '—',
//         'Emergency Contact Number': item.EmergencyContactNumber || '—',
//         'PG Name': item.PGName || '—',
//         'Room Number': item.RoomNumber || '—',
//         'Bed Number': item.BedNumber || '—',
//         'Joining Date': item.JoinDate || '—',
//         'Notice Date': item.NoticeDate || '—',
//         'Vacate Date': item.VacateDate || '—',
//         'Exit Date': item.ExitDate || '—'
//       }))

//       const pgName = distinctTenants[0]?.PGName || 'PG'
//       const fileName = `All_Tenant_Register_${pgName}_${today.toISOString().split('T')[0]}`
//       downloadExcel(excelData, fileName)
      
//     } catch (error) {
//       console.error('Error downloading all tenant register:', error)
//       alert('Failed to download tenant register data: ' + (error.message || 'Unknown error'))
//     } finally {
//       setDownloadingAll(false)
//     }
//   }

//   if (loading) {
//     return (
//       <div className="oc-root">
//         <div className="oc-loading">
//           <div className="oc-spinner"></div>
//           <p>Loading compliance data...</p>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="oc-root">
//       <div className="oc-filter-bar">
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Mobile</label>
//           <input className="oc-filter-input" placeholder="Search mobile..." value={search.mobile}
//             onChange={e => setSearch(p => ({ ...p, mobile: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Room</label>
//           <input className="oc-filter-input" placeholder="Room no." value={search.room}
//             onChange={e => setSearch(p => ({ ...p, room: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Tenant</label>
//           <input className="oc-filter-input" placeholder="Name..." value={search.tenant}
//             onChange={e => setSearch(p => ({ ...p, tenant: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">From</label>
//           <div className="oc-date-wrap">
//             {/* <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
//               <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
//             </svg> */}
//             <input type="date" className="oc-filter-input oc-date-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
//           </div>
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">To</label>
//           <div className="oc-date-wrap">
//             {/* <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
//               <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
//             </svg> */}
//             <input type="date" className="oc-filter-input oc-date-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
//           </div>
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Status</label>
//           <select className="oc-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
//             <option value="all">All</option>
//             <option value="active">Active</option>
//             <option value="previous">Previous</option>
//           </select>
//         </div>

//         {/* Download All Button */}
//         <button 
//           className="oc-download-all-btn"
//           onClick={handleDownloadAll}
//           disabled={downloadingAll}
//         >
//           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//             <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
//           </svg>
//           {downloadingAll ? 'Downloading...' : 'Download All'}
//         </button>
//       </div>

//       {(statusFilter === 'all' || statusFilter === 'active') && (
//         <div className="oc-section">
//           <div className="oc-section-title-row">
//             <h3 className="oc-section-title">Active Tenants</h3>
//             <span className="oc-count-badge">{filteredActive.length}</span>
//           </div>
//           {filteredActive.length === 0 ? (
//             <div className="oc-table-empty">
//               <p>No active tenants found</p>
//             </div>
//           ) : (
//             <div className="oc-records-wrap">
//               <RecordsHead />
//               {filteredActive.map(item => (
//                 <TenantRecordRow 
//                   key={item.ComplianceId} 
//                   item={item} 
//                   onViewAgreement={onViewAgreement}
//                   onViewTenantRegister={onViewTenantRegister}
//                 />
//               ))}
//             </div>
//           )}
//         </div>
//       )}

//       {(statusFilter === 'all' || statusFilter === 'previous') && filteredPrevious.length > 0 && (
//         <div className="oc-section">
//           <div className="oc-section-title-row">
//             <h3 className="oc-section-title">Previous Tenants</h3>
//             <span className="oc-count-badge oc-count-muted">{filteredPrevious.length}</span>
//           </div>
//           <div className="oc-accordion">
//             {sortedMonths.map(month => {
//               const items = byMonth[month]
//               const isOpen = openMonths[month]
//               return (
//                 <div key={month} className="oc-accordion-item">
//                   <button className="oc-accordion-head" onClick={() => toggleMonth(month)}>
//                     <div className="oc-accordion-title">
//                       <span className="oc-accordion-month">{month}</span>
//                       <span className="oc-accordion-count">{items.length} tenant{items.length !== 1 ? 's' : ''}</span>
//                     </div>
//                     <svg className={`oc-chevron ${isOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
//                       <path d="M3 5L7 9L11 5" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round"/>
//                     </svg>
//                   </button>
//                   {isOpen && (
//                     <div className="oc-accordion-body">
//                       <RecordsHead />
//                       {items.map(item => (
//                         <TenantRecordRow 
//                           key={item.ComplianceId} 
//                           item={item} 
//                           onViewAgreement={onViewAgreement}
//                           onViewTenantRegister={onViewTenantRegister}
//                         />
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               )
//             })}
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }

// // ─── Main ─────────────────────────────────────────────────────────
// const OwnerCompliance = ({ selectedPG, selectedPGId }) => {
//   const [activeTab, setActiveTab] = useState('pending')
//   const [complianceData, setComplianceData] = useState([])
//   const [loading, setLoading] = useState(false)
//   const [error, setError] = useState(null)
//   const [showAgreement, setShowAgreement] = useState(false)
//   const [agreementFileName, setAgreementFileName] = useState('')
  
//   const [showTenantRegister, setShowTenantRegister] = useState(false)
//   const [tenantRegisterData, setTenantRegisterData] = useState(null)
//   const [tenantRegisterLoading, setTenantRegisterLoading] = useState(false)

//   useEffect(() => {
//     if (selectedPGId) {
//       fetchComplianceData()
//     }
//   }, [selectedPGId])

//   const fetchComplianceData = async () => {
//     setLoading(true)
//     setError(null)
//     try {
//       const response = await getAllComplianceByPGID(selectedPGId)
//       if (response && response.length > 0) {
//         setComplianceData(response)
//       } else {
//         setComplianceData([])
//       }
//     } catch (err) {
//       console.error('Error fetching compliance data:', err)
//       setError('Failed to load compliance data')
//       setComplianceData([])
//     } finally {
//       setLoading(false)
//     }
//   }

//   const handleUpdateStatus = async (complianceId, newStatus) => {
//     console.log("Updating compliance status:", { complianceId, newStatus });
    
//     try {
//       const response = await updateComplianceStatus({
//         ComplianceID: parseInt(complianceId),
//         Status: newStatus
//       })
//       console.log("response from updateComplianceStatus:", response);
      
//       if (response.Success === 1) {
//         await fetchComplianceData()
//         alert('Status updated successfully!')
//       } else {
//         alert(response.Message || 'Failed to update status')
//       }
//     } catch (err) {
//       console.error('Error updating status:', err)
//       alert('Failed to update status')
//     }
//   }

//   const handleViewAgreement = (item) => {
//     const fileName = `Rent_Agreement_Room-${item.RoomNumber}_${(item.tenantName || 'Tenant').replace(/\s+/g, '_')}.pdf`
//     setAgreementFileName(fileName)
//     setShowAgreement(true)
//   }

//   const handleViewTenantRegister = async (item) => {
//     const admissionId = item.AdmissionID || item.AdmissionId
    
//     if (!admissionId) {
//       alert('No Admission ID found for this tenant')
//       return
//     }

//     setShowTenantRegister(true)
//     setTenantRegisterLoading(true)
//     setTenantRegisterData(null)

//     try {
//       const today = new Date()
//       const fromDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())
//         .toISOString().split('T')[0]
//       const toDate = today.toISOString().split('T')[0]
      
//       const pgId = selectedPGId

//       console.log('Fetching tenant register with:', { pgId, admissionId, fromDate, toDate })
      
//       const response = await getTenantRegisterByAdmission(pgId, admissionId, fromDate, toDate)
//       console.log('Tenant Register Response:', response)
      
//       if (response.success) {
//         setTenantRegisterData(response.tenants || response.data || response)
//       } else {
//         alert(response.message || 'Failed to fetch tenant details')
//         setShowTenantRegister(false)
//       }
//     } catch (err) {
//       console.error('Error fetching tenant register:', err)
//       alert('Failed to fetch tenant details')
//       setShowTenantRegister(false)
//     } finally {
//       setTenantRegisterLoading(false)
//     }
//   }

//   if (error) {
//     return (
//       <div className="ocomp-root">
//         <div className="oc-error">
//           <p>{error}</p>
//           <button onClick={fetchComplianceData} className="oc-retry-btn">Retry</button>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="ocomp-root">
//       <div className="ocomp-tabs">
//         <button className={`ocomp-tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
//           Pending Requests
//         </button>
//         <button className={`ocomp-tab ${activeTab === 'records' ? 'active' : ''}`} onClick={() => setActiveTab('records')}>
//           Tenant Compliance Records
//         </button>
//       </div>
      
//       {activeTab === 'pending' && (
//         <PendingRequests 
//           complianceData={complianceData}
//           onUpdateStatus={handleUpdateStatus}
//           loading={loading}
//         />
//       )}
      
//       {activeTab === 'records' && (
//         <TenantRecords 
//           complianceData={complianceData}
//           loading={loading}
//           onViewAgreement={handleViewAgreement}
//           onViewTenantRegister={handleViewTenantRegister}
//           selectedPGId={selectedPGId}
//         />
//       )}

//       {showAgreement && (
//         <ViewAgreementModal
//           fileName={agreementFileName}
//           onClose={() => setShowAgreement(false)}
//         />
//       )}

//       {showTenantRegister && (
//         <TenantRegisterModal
//           tenantData={tenantRegisterData}
//           loading={tenantRegisterLoading}
//           onClose={() => {
//             setShowTenantRegister(false)
//             setTenantRegisterData(null)
//           }}
//         />
//       )}
//     </div>
//   )
// }

// export default OwnerCompliance